import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bus, RefreshCw } from "lucide-react";

import { DecisionsCard, ViewerNote } from "../../components/dashboard/DeskParts";
import { longDay, shortDay } from "../../components/dashboard/deskLogic";
import { fleetConfig, paperTone, routeLoad, tripConfig, tripTone, viewerNote, type FleetDesk, type FleetVehicle, type TodayTrip } from "../../components/fleet/fleetLogic";
import { ErrorState, LoadingState } from "../../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../../components/register/ClientRegister";
import { api } from "../../services/api";

/**
 * The transport manager's dashboard: what needs doing, today's trips, the
 * fleet with its papers and repairs, and each route's load. Trips, riders
 * and vehicles are managed on the Transport page.
 */
export default function TransportManagerDashboard() {
  const query = useQuery({
    queryKey: ["transport-desk"],
    queryFn: async () => (await api.get("/dashboard/transport-manager/")).data as FleetDesk,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the transport dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The transport dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const tripColumns: RegisterColumn<TodayTrip>[] = [
    { header: "Leaves", cell: (row) => <span className="tabular-nums">{row.departure}</span>, exportValue: (row) => row.departure },
    { header: "Route", cell: (row) => <span className="font-medium text-gray-900">{row.route}</span>, exportValue: (row) => row.route },
    { header: "Vehicle", cell: (row) => row.vehicle, exportValue: (row) => row.vehicle },
    { header: "Driver", cell: (row) => row.driver || <span className="text-amber-700">None</span>, exportValue: (row) => row.driver },
    { header: "On board", cell: (row) => `${row.onboard} of ${row.capacity}`, exportValue: (row) => row.onboard, align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...tripTone(row)} />, exportValue: (row) => tripTone(row).label },
    { header: "Arrives", cell: () => "", exportValue: (row) => row.arrival, exportOnly: true },
  ];
  const fleetColumns: RegisterColumn<FleetVehicle>[] = [
    { header: "Vehicle", cell: (row) => <span><span className="block font-medium text-gray-900">{row.name}</span><span className="text-xs text-gray-500">{row.registration}</span></span>, exportValue: (row) => `${row.name} (${row.registration})` },
    { header: "Seats", cell: (row) => row.seats, exportValue: (row) => row.seats, align: "right" },
    { header: "Driver", cell: (row) => row.driver || <span className="text-amber-700">None assigned</span>, exportValue: (row) => row.driver },
    { header: "Papers", cell: (row) => <span><StatusDot {...paperTone(row, data.today)} />{row.next_paper_expiry && <span className="block text-xs text-gray-500">{shortDay(row.next_paper_expiry)}</span>}</span>, exportValue: (row) => `${paperTone(row, data.today).label}${row.next_paper_expiry ? ` (${row.next_paper_expiry})` : ""}` },
    { header: "Repairs open", cell: (row) => (row.open_repairs ? <span className="font-semibold text-amber-800">{row.open_repairs}</span> : "—"), exportValue: (row) => row.open_repairs, align: "right" },
    { header: "Status", cell: (row) => (row.status === "maintenance" ? "In the workshop" : "In service"), exportValue: (row) => row.status },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transport</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)} · trips, the fleet and the routes at a glance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/transport" className="btn btn-primary gap-1.5"><Bus className="h-4 w-4" /> Open transport</Link>
          <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <ViewerNote text={viewerNote(data.viewer_role)} />
      <DecisionsCard decisions={data.work} title="Work waiting" where="in transport" />

      <ClientRegister
        title="Today's trips"
        description="Every trip on the road today, in departure order. A trip past its departure time and not yet off counts as late."
        actions={<Link to="/transport?view=today" className="btn btn-outline h-9 text-sm">Open trips</Link>}
        rows={data.trips}
        config={tripConfig}
        columns={tripColumns}
        rowKey={(row) => row.id}
        noun={["trip", "trips"]}
        exportName={{ file: `trips-${data.today}`, title: `Trips, ${longDay(data.today)}` }}
        emptyText="No trips are scheduled today."
      />

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <ClientRegister
            title="The fleet"
            description="Vehicles in service with their driver, the next insurance or permit to expire, and repairs still open."
            actions={<Link to="/transport?tab=vehicles" className="btn btn-outline h-9 text-sm">Vehicles</Link>}
            rows={data.fleet}
            config={fleetConfig(data.today)}
            columns={fleetColumns}
            rowKey={(row) => row.id}
            noun={["vehicle", "vehicles"]}
            exportName={{ file: "fleet", title: "The fleet" }}
            emptyText="No vehicles are in service."
          />
        </div>
        <section className="card self-start">
          <div className="card-titlebar"><div><h2 className="font-semibold text-gray-900">Routes</h2><p className="text-xs text-gray-500">Riders against places</p></div><Link to="/transport?tab=routes" className="text-xs font-medium text-primary-700 hover:underline">Routes</Link></div>
          {data.routes.length ? (
            <ul className="divide-y divide-gray-100">
              {data.routes.map((route) => {
                const load = routeLoad(route);
                return (
                  <li key={route.id} className="px-6 py-3 text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="min-w-0 truncate font-medium text-gray-900">{route.name}</span>
                      <span className="shrink-0 tabular-nums text-gray-600">{route.riders} of {route.capacity}</span>
                    </div>
                    <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-gray-100"><span className={`block h-full rounded-full ${load.full ? "bg-red-500" : "bg-primary-500"}`} style={{ width: `${Math.min(load.share, 100)}%` }} /></span>
                    <span className="mt-1 block text-xs text-gray-500">
                      Leaves {route.departure} · {load.full ? "full" : `${load.free} ${load.free === 1 ? "place" : "places"} free`}
                      {route.waiting ? <span className={load.overbooked ? "font-medium text-amber-800" : ""}> · {route.waiting} waiting{load.overbooked ? ", more than the places left" : ""}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No routes are running.</p>}
        </section>
      </div>
    </div>
  );
}
